import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from '../utils/supabaseClient';

/**
 * Full, irreversible account deletion. Requires re-auth (same
 * signInWithPassword gate as usePasswordChange.js/useEmailChange.js) plus
 * typing "DELETE" — stronger than other confirmations in the app because
 * this one permanently removes the teacher's students, lessons, chat
 * history, homework and subscription (see delete-account Edge Function).
 */
export function useAccountDeletion() {
  const navigate = useNavigate();
  const [showConfirm, setShowConfirm] = useState(false);
  const [password, setPassword] = useState('');
  const [confirmText, setConfirmText] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState('');

  const openConfirm = () => {
    setPassword('');
    setConfirmText('');
    setError('');
    setShowConfirm(true);
  };

  const closeConfirm = () => {
    if (deleting) return;
    setShowConfirm(false);
    setPassword('');
    setConfirmText('');
    setError('');
  };

  const handleDelete = async () => {
    setError('');
    if (confirmText !== 'DELETE') {
      setError('Type DELETE to confirm.');
      return;
    }
    if (!password) {
      setError('Enter your password to confirm.');
      return;
    }
    setDeleting(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user?.email) throw new Error('Not authenticated.');

      const { error: signInErr } = await supabase.auth.signInWithPassword({
        email: user.email,
        password,
      });
      if (signInErr) {
        setError('Current password is incorrect.');
        return;
      }

      const { error: fnError } = await supabase.functions.invoke('delete-account');
      if (fnError) throw fnError;

      try { localStorage.removeItem('currentUser'); } catch (_) {}
      try { localStorage.removeItem('isAuthenticated'); } catch (_) {}
      try { await supabase.auth.signOut(); } catch (_) {}
      navigate('/login');
    } catch (e) {
      setError(e?.message || 'Could not delete account. Try again.');
    } finally {
      setDeleting(false);
    }
  };

  return {
    showConfirm, openConfirm, closeConfirm,
    password, setPassword,
    confirmText, setConfirmText,
    deleting, error,
    handleDelete,
  };
}
